import { useCallback, useEffect, useState } from "react";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import { GenericConverter } from "utils/firestoreConverters";
import { clearPersistedCache, readPersistedCache, writePersistedCache } from "utils/persistedCache";
import {
  dedupeLocations,
  stripGroupProvenance,
  validateLocationGroup
} from "utils/locations/locationGroups";
import {
  LOCATION_GROUPS_SUBCOLLECTION,
  MAX_LOCATION_GROUPS_PER_USER
} from "./../@types/sharedSchema";
import type { LocationGroup, ProcessedListingAddress } from "./../@types/sharedSchema";

export type LocationGroupInput = {
  /** Absent = create. Present = edit that exact document. */
  id?: string;
  name: string;
  emoji?: string;
  locations: ProcessedListingAddress[];
};

export type UseLocationGroupsResult = {
  groups: LocationGroup[];
  /**
   * TRUE only when there is genuinely nothing to render yet — no live snapshot and
   * no persisted copy. A warm cache reports `false` immediately, so opening a
   * picker never flashes a spinner over a list it already has.
   */
  loading: boolean;
  /** An i18n key when we recognise the failure, otherwise the raw Firestore message. Always logged. */
  error: string | null;
  canAddGroup: boolean;
  saveGroup: (input: LocationGroupInput) => Promise<LocationGroup>;
  deleteGroup: (groupId: string) => Promise<void>;
};

/** validate → the i18n key the caller renders. The rule lives in the pure util; the wording lives here. */
const VALIDATION_ERROR_KEYS = {
  "name-empty": "locationGroups.errors.nameEmpty",
  "name-too-long": "locationGroups.errors.nameTooLong",
  "no-locations": "locationGroups.errors.noLocations",
  "too-many-locations": "locationGroups.errors.tooManyLocations"
} as const;

const PERSIST_KEY = "webot:locationGroups:v1";

/**
 * The realtor's saved location groups, live and shared.
 *
 * ── Why a subcollection ──────────────────────────────────────────────────────
 *
 * `dashboard-users-v2/{userEmail}/location-groups/{groupId}`, one document per
 * group. `firestore.rules` already grants read+write on
 * `dashboard-users-v2/{userEmail}/{document=**}`, so this needs no rules change;
 * every `dashboard-users-v2` sub-trigger in the functions repo is path-specific
 * (`contacts`, `sentMessages`, `listingData`), so it fires none of them; and it
 * keeps 40x50 locations off the user document, which `addUserToRequest` reads on
 * every authenticated server request and `FirestoreContext` live-snapshots.
 *
 * ── ⚠ ONE SUBSCRIPTION PER ACCOUNT, NOT ONE PER CONSUMER ─────────────────────
 *
 * This started life as a plain `onSnapshot` inside the hook body, which meant one
 * Firestore listen per mounted consumer AND a fresh listen every time a consumer
 * mounted. Six components use this hook, and one of them is the `LocationPicker`
 * inside the contact dialog — so the listen STARTED when that dialog opened, the
 * realtor clicked the groups tab a second later, and the panel sat empty until the
 * round trip came back. Closing the dialog dropped the last subscriber, Firestore
 * released the target, and the next open paid for all of it again. Reported as
 * 10-20 seconds of blank panel.
 *
 * Two app-level facts make that cold path much worse than it sounds, and neither
 * is fixable from here:
 *
 *  - `firebaseConfig` never calls `enablePersistence`, so there is NO on-device
 *    Firestore cache. Every cold listen is a network round trip; the earlier
 *    comment in this file claiming a snapshot "resolves from cache within a
 *    frame" was simply false for this app.
 *  - `db.settings({ experimentalAutoDetectLongPolling: true })` makes the SDK
 *    race WebSocket against long-polling on the first connection of a session,
 *    which is exactly when a just-mounted listener is waiting.
 *
 * So the subscription is now module-level, keyed to the account, started by the
 * first consumer and DELIBERATELY NOT torn down when that consumer unmounts. It
 * is released only when the account changes or on sign-out. The leak is one
 * listener over at most `MAX_LOCATION_GROUPS_PER_USER` tiny documents, held for
 * the session — the same trade `useOrgDirectory` makes, and the reason a second
 * picker opens instantly.
 *
 * ── AND IT SURVIVES A RELOAD ─────────────────────────────────────────────────
 *
 * A module cache is worth nothing across a hard reload, which is the moment it is
 * needed most. The last snapshot is kept in `localStorage` and used as the first
 * render's value, with the live listener demoted to correcting it a moment later.
 * See `persistedCache.ts` for what makes that safe: stamped with the account,
 * expiring, and never written from a failure. Nothing here is an access decision.
 *
 * ── ⚠ A group is written in ONE `set()`, never created-then-filled ───────────
 *
 * The generic metadata Cloud Function is registered on level-2 paths, so it WOULD
 * fire here — which is why `location-groups` is in `METADATA_STAMP_EXCLUDED_PATHS`.
 * `handleOnCreateDocument` re-merges the CREATE-TIME snapshot 0.5-6.5s after a
 * create, so with it enabled a realtor who added one more area right after creating
 * a group would silently lose it, and a group created and immediately deleted would
 * be RESURRECTED by that late `set()`.
 *
 * Consequences, both handled below: the timestamps are written here rather than
 * stamped, and the edit stamp is `lastEditedAt` rather than `updatedTime` because
 * `GenericConverter.toFirestore` strips the latter on write.
 */

type Subscriber = (groups: LocationGroup[]) => void;

let cache: LocationGroup[] | null = null;
/** The account the cache and the listener belong to — a sign-in must never inherit them. */
let cachedFor: string | null = null;
let releaseSnapshot: (() => void) | null = null;

const subscribers = new Set<Subscriber>();

const normalizeEmail = (value?: string | null): string => (value ?? "").trim().toLowerCase();

/** Hebrew-aware, because Firestore orders by UTF-8 bytes and a realtor does not. */
const byName = (groups: LocationGroup[]): LocationGroup[] =>
  [...groups].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "he"));

const publish = (groups: LocationGroup[], forEmail: string): void => {
  cache = groups;
  cachedFor = forEmail;
  // Only ever from a SUCCESSFUL snapshot, so a permission error or a dropped
  // connection can never be persisted as "this user has no groups".
  writePersistedCache(PERSIST_KEY, forEmail, groups);
  subscribers.forEach((subscriber) => subscriber(groups));
};

/**
 * Undo the JSON round trip's damage to the two timestamps.
 *
 * `GenericConverter` hands the live snapshot real `Date`s, but `persistedCache`
 * goes through `JSON.stringify`, which renders them as ISO strings and gives back
 * strings on read. `saveGroup` carries `createdTime` forward from whatever is
 * cached, so without this an edit made after a reload would write a STRING into a
 * timestamp field — and `convertFirestoreDatesToJsDates` would not convert it back,
 * so the type would quietly start lying.
 */
const reviveDates = (groups: LocationGroup[]): LocationGroup[] =>
  groups.map((group) => ({
    ...group,
    ...(group.createdTime ? { createdTime: new Date(group.createdTime) } : {}),
    ...(group.lastEditedAt ? { lastEditedAt: new Date(group.lastEditedAt) } : {})
  }));

/**
 * The cached groups, or null when nothing is known for this account yet.
 *
 * Falls back to the persisted copy and ADOPTS it into the module cache, so the
 * first render after a reload already has the list and reports `loading: false`.
 */
const getCachedGroups = (email: string): LocationGroup[] | null => {
  if (!email) return null;
  if (cache && cachedFor === email) return cache;

  const persisted = readPersistedCache<LocationGroup[]>(PERSIST_KEY, email);
  if (!persisted) return null;
  cache = reviveDates(persisted.value);
  cachedFor = email;
  return cache;
};

const teardown = (): void => {
  releaseSnapshot?.();
  releaseSnapshot = null;
  cache = null;
  cachedFor = null;
};

const collectionFor = (email: string) =>
  db
    .collection("dashboard-users-v2")
    .doc(email)
    .collection(LOCATION_GROUPS_SUBCOLLECTION)
    .withConverter(GenericConverter<LocationGroup>());

/**
 * Starts the one listener for this account, if it is not already running.
 * Idempotent, so every consumer can call it on mount.
 */
const ensureSubscribed = (email: string, onError: (message: string) => void): void => {
  if (releaseSnapshot && cachedFor === email) return;
  // A different account: drop the previous listener and everything it cached.
  if (releaseSnapshot) teardown();

  cachedFor = email;
  releaseSnapshot = collectionFor(email).onSnapshot(
    (snapshot) => publish(byName(snapshot.docs.map((doc) => doc.data())), email),
    (err) => {
      console.error("useLocationGroups snapshot error", err);
      onError(err?.message ?? "locationGroups.errors.saveFailed");
    }
  );
};

/**
 * Drops the cached groups and the listener. Call on sign-out; the next account
 * cannot be handed the previous one's groups even for a frame.
 */
export const invalidateLocationGroups = (): void => {
  teardown();
  // The persisted copy goes too — clearing only memory would be undone by the very
  // next read, which falls back to storage.
  clearPersistedCache(PERSIST_KEY);
  subscribers.forEach((subscriber) => subscriber([]));
};

export default function useLocationGroups(): UseLocationGroupsResult {
  const { user } = useAuth();
  const email = normalizeEmail(user?.email);

  const [groups, setGroups] = useState<LocationGroup[]>(() => getCachedGroups(email) ?? []);
  const [loading, setLoading] = useState<boolean>(
    () => Boolean(email) && getCachedGroups(email) === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    subscribers.add(setGroups);
    return () => {
      // Only this consumer stops listening. The Firestore subscription stays up —
      // see the header: tearing it down here is what made re-opening a dialog wait
      // for a fresh round trip.
      subscribers.delete(setGroups);
    };
  }, []);

  useEffect(() => {
    if (!email) {
      invalidateLocationGroups();
      setGroups([]);
      setLoading(false);
      return;
    }

    // Show the persisted answer first; the listener corrects it a moment later.
    const warm = getCachedGroups(email);
    if (warm) {
      setGroups(warm);
      setLoading(false);
    } else {
      setLoading(true);
    }

    setError(null);
    ensureSubscribed(email, setError);
  }, [email]);

  useEffect(() => {
    // The first snapshot for a cold account is what ends the spinner.
    if (groups.length || cachedFor === email) setLoading(false);
  }, [groups, email]);

  const canAddGroup = groups.length < MAX_LOCATION_GROUPS_PER_USER;

  const saveGroup = useCallback(
    async (input: LocationGroupInput): Promise<LocationGroup> => {
      if (!email) throw new Error("locationGroups.errors.saveFailed");

      const name = (input?.name ?? "").trim();
      // Deduped before validation so a group that only LOOKS over the cap because
      // the same area was picked twice is accepted rather than refused.
      const locations = dedupeLocations(input?.locations ?? []).map(stripGroupProvenance);

      // `"reason" in` rather than `!validation.ok`: this repo compiles with
      // `strict: false`, under which the boolean-literal discriminant does not
      // narrow the union.
      const validation = validateLocationGroup({ name, locations });
      if ("reason" in validation) throw new Error(VALIDATION_ERROR_KEYS[validation.reason]);

      const collection = collectionFor(email);
      const isNew = !input.id;
      if (isNew && (cache ?? groups).length >= MAX_LOCATION_GROUPS_PER_USER) {
        throw new Error("locationGroups.errors.tooManyGroups");
      }

      const docRef = input.id ? collection.doc(input.id) : collection.doc();
      const emoji = (input?.emoji ?? "").trim();

      // `set()` without merge REPLACES the document, so an edit must carry the
      // original creation time forward or it would be rewritten on every save.
      // It comes from the cache we already hold — no extra read.
      const now = new Date();
      const existing = input.id
        ? (cache ?? groups).find((group) => group.id === input.id)
        : undefined;

      const group: LocationGroup = {
        id: docRef.id,
        name,
        locations,
        // Omitted rather than set to undefined: `set()` without merge replaces the
        // document, so an omitted emoji is genuinely cleared on an edit — and
        // `ignoreUndefinedProperties` would have dropped an explicit undefined
        // without removing the stored value.
        ...(emoji ? { emoji } : {}),
        createdTime: existing?.createdTime ?? now,
        lastEditedAt: now
      };

      try {
        await docRef.set(group);
      } catch (err) {
        console.error("useLocationGroups save error", err);
        throw new Error("locationGroups.errors.saveFailed");
      }

      // The listener republishes on its own; this only removes the round trip
      // between "saved" and the list showing it.
      publish(byName([...(cache ?? groups).filter((g) => g.id !== group.id), group]), email);

      return group;
    },
    [email, groups]
  );

  const deleteGroup = useCallback(
    async (groupId: string): Promise<void> => {
      if (!email || !groupId) throw new Error("locationGroups.errors.deleteFailed");

      try {
        await collectionFor(email).doc(groupId).delete();
      } catch (err) {
        console.error("useLocationGroups delete error", err);
        throw new Error("locationGroups.errors.deleteFailed");
      }

      publish(
        (cache ?? groups).filter((group) => group.id !== groupId),
        email
      );
    },
    [email, groups]
  );

  return { groups, loading, error, canAddGroup, saveGroup, deleteGroup };
}
